import { Pagination } from "@/components/shared/Pagination";
import { Icon } from "@/lib/icons";
import { ExcelFileRow } from "./ExcelFileRow";
import type { ExcelFileInfo } from "@/api/excel-import.contract";

interface ExcelFilesTableProps {
  files: ExcelFileInfo[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  total: number;
  labels: {
    phrases: string;
    fileUnknown: string;
    loading: string;
    noFiles: string;
    updateFile: string;
    excelFile: string;
    updating: string;
    update: string;
    deleteFileTitle: string;
    deleteFileDescription: string;
    delete: string;
    cancel: string;
    colFile: string;
    colLanguage: string;
    colAuthor: string;
    colDate: string;
    colActions: string;
    status: string;
    pending: string;
    approved: string;
    rejected: string;
    approve: string;
    reject: string;
    rejectPrompt: string;
    preview: string;
    previewLoading: string;
    previewError: string;
    previewEmpty: string;
    previewSummary: string;
  };
  updatingFileId: number | null;
  selectedFile: File | null;
  isUpdateDialogOpen: boolean;
  isDownloading: boolean;
  isUpdating: boolean;
  deletingFileId: number | null;
  isSuperAdmin: boolean;
  isApproving: boolean;
  isRejecting: boolean;
  onDownload: (id: number) => void;
  onOpenUpdate: (id: number) => void;
  onCloseUpdate: () => void;
  onFileSelect: (file: File | null) => void;
  onUpdateSubmit: (e: React.FormEvent) => void;
  onDelete: (id: number) => void;
  onApprove: (id: number) => void;
  onReject: (id: number, reason: string) => void;
  onPage: (page: number) => void;
}

export function ExcelFilesTable({
  files,
  isLoading,
  page,
  totalPages,
  total,
  labels,
  updatingFileId,
  selectedFile,
  isUpdateDialogOpen,
  isDownloading,
  isUpdating,
  deletingFileId,
  isSuperAdmin,
  isApproving,
  isRejecting,
  onDownload,
  onOpenUpdate,
  onCloseUpdate,
  onFileSelect,
  onUpdateSubmit,
  onDelete,
  onApprove,
  onReject,
  onPage,
}: ExcelFilesTableProps) {
  return (
    <section className="pt-7">
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Icon name="refreshCw" className="mr-3 h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{labels.loading}</span>
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="fileText" className="mb-3 h-8 w-8 text-muted-foreground/80" />
          <p className="text-sm text-muted-foreground">{labels.noFiles}</p>
        </div>
      ) : (
        // Header and rows share these columns (subgrid), so the actions
        // column takes the width of its widest row on every row.
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.5fr)_minmax(0,0.6fr)_minmax(0,0.6fr)_minmax(0,0.45fr)_auto] gap-x-4">
          <div className="col-span-full grid grid-cols-subgrid items-center border-b border-border/40 pb-3">
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colFile}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colLanguage}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colAuthor}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colDate}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.status}</span>
            <span className="text-right text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colActions}</span>
          </div>
          {files.map((file) => (
            <ExcelFileRow
              key={file.id}
              file={file}
              labels={labels}
              isUpdateDialogOpen={isUpdateDialogOpen}
              updatingFileId={updatingFileId}
              selectedFile={selectedFile}
              isDownloading={isDownloading}
              isUpdating={isUpdating}
              isDeleting={deletingFileId === file.id}
              onDownload={onDownload}
              onOpenUpdate={onOpenUpdate}
              onCloseUpdate={onCloseUpdate}
              onFileSelect={onFileSelect}
              onUpdateSubmit={onUpdateSubmit}
              onDelete={onDelete}
              isSuperAdmin={isSuperAdmin}
              isApproving={isApproving}
              isRejecting={isRejecting}
              onApprove={onApprove}
              onReject={onReject}
            />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} total={total} onPage={onPage} />
    </section>
  );
}
